import { Redo2, Undo2 } from "lucide-react";

import { trackImageEditToolRedo, trackImageEditToolUndo } from "@design-lab/shared/lib/analytics";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { useRefineHistoryContext } from "@design-lab/widgets/artboard/model/RefineHistoryContext";
import { RefineToolTabs } from "./RefineToolTabs";

type RefineHeaderProps = {
  activeTool: string;
  onToolChange: (tool: string) => void;
  artworkId?: string;
};

export function RefineHeader({ activeTool, onToolChange, artworkId }: RefineHeaderProps) {
  const { canUndo, canRedo, undo, redo } = useRefineHistoryContext();

  const handleUndo = () => {
    if (artworkId) {
      trackImageEditToolUndo(artworkId);
    }
    undo();
  };

  const handleRedo = () => {
    if (artworkId) {
      trackImageEditToolRedo(artworkId);
    }
    redo();
  };

  return (
    <div className="absolute left-0 right-0 top-0 z-10">
      <div className="flex w-full items-center justify-between bg-white p-2 shadow-md">
        <div className="flex-1">
          <h2 className="text-lg font-medium text-gray-800">Edit Artwork</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onPress={handleUndo}
            isDisabled={!canUndo}
            className="box-border flex aspect-square items-center justify-center rounded-lg border border-blue-700 bg-white px-3 py-2.5 text-base leading-6 text-blue-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-transparent disabled:bg-gray-100 disabled:text-gray-600 disabled:opacity-70"
            data-testid="refine-undo"
            aria-label="Undo (Cmd/Ctrl + Z)"
            size="lg"
            variant="icon"
          >
            <Undo2 className="size-5" />
          </Button>
          <Button
            onPress={handleRedo}
            isDisabled={!canRedo}
            className="box-border flex aspect-square items-center justify-center rounded-lg border border-blue-700 bg-white px-3 py-2.5 text-base leading-6 text-blue-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-transparent disabled:bg-gray-100 disabled:text-gray-600 disabled:opacity-70"
            data-testid="refine-redo"
            aria-label="Redo (Cmd/Ctrl + Shift + Z)"
            size="lg"
            variant="icon"
          >
            <Redo2 className="size-5" />
          </Button>
        </div>
        <div className="flex flex-1 justify-end"></div>
      </div>

      <div className="mt-3">
        <RefineToolTabs activeTool={activeTool} onToolChange={onToolChange} />
      </div>
    </div>
  );
}
